import { useRouteLoaderData } from '@remix-run/react';
import type {
  NavbarDocument,
  NavbarSerializableNode,
  SerializableNode,
  TSectionElement,
} from '@shared/dream-components';
import { constructMobileView, navbarV2Migrator } from '@shared/dream-components';
import { useEffect, useState } from 'react';

import type { Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';

import isUserAgentMobile from '~/utils/isUserAgentMobile';

import type { RootLoaderData } from '~/root';

import NavbarRendererV2 from './NavbarRenderer';

interface NavbarSerializerProps {
  site: Site;
  page?: Page;
}

interface NavbarSerializerRenderProps {
  data: NavbarSerializableNode;
  isMobile: boolean;
}

declare global {
  interface Window {
    opera: any;
  }

  interface Navigator {
    userAgentData: {
      mobile: boolean;
    };
  }
}

const isMobileDevice = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (isUserAgentMobile(navigator.userAgent || navigator.vendor || window.opera)) {
    return true;
  }

  if (typeof navigator !== 'undefined' && navigator?.userAgentData?.mobile) {
    return true;
  }

  return (window?.innerWidth || 1024) < 768;
};

// Custom hook for detecting mobile viewport
const useIsMobile = (defaultValue: boolean) => {
  const [isMobile, setIsMobile] = useState<boolean>(defaultValue);

  useEffect(() => {
    setIsMobile(isMobileDevice());

    window?.addEventListener('resize', () => {
      setIsMobile(isMobileDevice());
    });
    return () =>
      window?.removeEventListener('resize', () => {
        setIsMobile(isMobileDevice());
      });
  }, []);

  return isMobile;
};

const serializeNode = (
  node: NavbarSerializableNode | NavbarSerializableNode[],
  screen: 'desktop' | 'mobile',
  parent?: NavbarSerializableNode,
) => {
  if (!node) return null;

  if (Array.isArray(node)) {
    return node.filter(Boolean).map((child, index) => (
      <NavbarRendererV2 key={`${index}-${child?.attrs?.id}`} node={child} screen={screen} parent={parent}>
        {'content' in child && Array.isArray(child.content) ? serializeNode(child.content, screen, child) : null}
      </NavbarRendererV2>
    ));
  }

  return (
    <NavbarRendererV2 node={node} screen={screen} parent={parent}>
      {'content' in node && Array.isArray(node.content) ? serializeNode(node.content, screen, node) : null}
    </NavbarRendererV2>
  );
};

const NavbarSerializerRender = ({ data, isMobile }: NavbarSerializerRenderProps) => {
  return serializeNode(data as NavbarSerializableNode | NavbarSerializableNode[], isMobile ? 'mobile' : 'desktop');
};

// Applies style and visibility settings for user button/dropdown
const applyUserButtonDropdown = (node: NavbarSerializableNode, site: Site, isMobile: boolean) => {
  if (Array.isArray(node)) {
    node.forEach((childNode) => {
      applyUserButtonDropdown(childNode, site, isMobile);
    });
    return;
  }

  // Handle User Button Dropdown Defaults
  if (
    node.attrs &&
    'type' in node.attrs &&
    node.attrs?.type === 'user_button' &&
    node.attrs?.action === 'profile' &&
    node.attrs?.style !== 'button'
  ) {
    // Only convert to dropdown if we have a theme and primary theme
    if (site?.theme_rules && site?.primary_theme) {
      node.attrs.style = 'dropdown';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
    } else if (node.attrs?.style === undefined) {
      // If we don't have a theme and the style is undefined, set it to button
      node.attrs.style = 'button';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
    }
  }

  // Recursively check children
  if ('content' in node && node.content && Array.isArray(node.content)) {
    node.content.forEach((childNode) => {
      applyUserButtonDropdown(childNode, site, isMobile);
    });
  }
};

const NavbarSerializerV2 = ({ site, page }: NavbarSerializerProps) => {
  const { isUAStringMobile } = useRouteLoaderData('root') as RootLoaderData;
  const navbarData = site.viewable_site_version.navbar;

  const isMobile = useIsMobile(isUAStringMobile);

  if (!navbarData || isMobile === undefined) {
    return null;
  }

  const firstSectionBackgroundColor = (
    (page?.viewable_page_version?.content as unknown as { content: SerializableNode[] })
      ?.content?.[0] as TSectionElement
  )?.attrs?.backgroundColor;

  if (navbarData.type === 'doc') {
    const typedNavbar = navbarData as NavbarDocument;
    const data = isMobile ? typedNavbar?.attrs?.data?.mobile : typedNavbar?.attrs?.data?.desktop;

    if (!data) {
      // Something went wrong with migration via the editor
      return null;
    }

    data.attrs.firstSectionBackgroundColor = firstSectionBackgroundColor;

    applyUserButtonDropdown(data, site, isMobile);

    return <NavbarSerializerRender isMobile={isMobile} data={data} />;
  }

  // Navbar v1 JSON, migrate and construct device specific view
  const migratedData = navbarV2Migrator(navbarData);

  const desktopView = migratedData;
  const mobileView = constructMobileView(migratedData);
  const data = isMobile ? mobileView : desktopView;

  applyUserButtonDropdown(data, site, isMobile);

  return <NavbarSerializerRender isMobile={isMobile} data={data} />;
};

export default NavbarSerializerV2;
